import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowRightIcon, BadgeCheckIcon, Loader2Icon, MailIcon } from 'lucide-react';
import { PageHeader } from '../components/PageHeader';
import { SiteFooter } from '../components/SiteFooter';
import { ChoiceChips } from '../components/ChoiceChips';
import { ACTIVATION_FORM_ENDPOINT, activationPayload, ACTIVATION_SUCCESS_URL, ActivationAnswers, activationMailto, emptyAnswers, featureOptions, heardFromOptions, prescriptionOptions, recommendOptions } from '../data/activation';
import { trackCustom } from '../utils/tracking';
type Status = 'idle' | 'submitting' | 'success' | 'error' | 'not_connected';
const ownerActions = [{
  title: 'Need a backup X1?',
  body: 'Keep a spare charged in the truck.',
  to: '/#offer'
}, {
  title: 'Equip your crew',
  body: 'Business / crew pack, one order.',
  to: '/#company'
}, {
  title: 'Join GritVue Crew Rewards',
  body: 'Share GritVue. Get paid.',
  to: '/rewards'
}];
const inputClass = 'mt-2 w-full border-2 border-white/20 bg-ink-950 px-4 py-3 text-[16px] text-offwhite placeholder:text-steel-500 focus:border-grit-500 focus:outline-none';
const labelClass = 'text-[11px] font-bold uppercase tracking-[0.2em] text-steel-400';
export function Activate() {
  const [answers, setAnswers] = useState<ActivationAnswers>(emptyAnswers);
  const [status, setStatus] = useState<Status>('idle');
  const started = useRef(false);
  useEffect(() => {
    window.scrollTo(0, 0);
    trackCustom('activation_page_visited');
  }, []);
  const update = (key: keyof ActivationAnswers, value: string) => {
    if (!started.current) {
      started.current = true;
      trackCustom('activation_started');
    }
    setAnswers(prev => ({
      ...prev,
      [key]: value
    }));
    if (status === 'error' || status === 'not_connected') setStatus('idle');
  };
  const canSubmit = answers.orderNumber.trim() !== '' && /\S+@\S+\.\S+/.test(answers.email);
  const submit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!canSubmit || status === 'submitting') return;
    // No backend yet: say so. Nothing is stored and nothing is faked.
    if (!ACTIVATION_FORM_ENDPOINT) {
      setStatus('not_connected');
      trackCustom('activation_not_connected');
      return;
    }
    setStatus('submitting');
    try {
      const response = await fetch(ACTIVATION_FORM_ENDPOINT, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json'
        },
        body: JSON.stringify(activationPayload(answers))
      });
      if (!response.ok) throw new Error(String(response.status));
      const result = (await response.json().catch(() => ({}))) as {
        success?: string | boolean;
      };
      if (result.success === false || result.success === 'false') throw new Error('not_delivered');
      trackCustom('activation_completed', {
        feature: answers.feature,
        heard_from: answers.heardFrom,
        recommend: answers.recommend,
        wears_prescription: answers.wearsPrescription
      });
      if (ACTIVATION_SUCCESS_URL) {
        window.location.href = ACTIVATION_SUCCESS_URL;
        return;
      }
      setStatus('success');
      window.scrollTo({
        top: 0,
        behavior: 'smooth'
      });
    } catch {
      setStatus('error');
    }
  };
  return <div className="min-h-full w-full bg-ink-950 font-sans text-offwhite" data-id="element-1673" data-mp="n81kx9uon9z4">
      <PageHeader data-id="element-1674" data-mp="j7rjmahu57ql" />
      <main className="mx-auto max-w-[1400px] px-4 py-14 sm:px-6 lg:grid lg:grid-cols-[0.9fr_1.1fr] lg:gap-16 lg:px-8 lg:py-20" data-id="element-1675" data-mp="j8aw3bopv3x8">
        <div className="lg:sticky lg:top-24 lg:self-start" data-id="element-1676" data-mp="98k3xgnmp10b">
          <p className="text-[11px] font-bold uppercase tracking-[0.22em] text-grit-500" data-id="element-1677" data-mp="9928d0kg2ljb">Owner activation</p>
          <h1 className="grit-headline mt-3 text-[clamp(2.5rem,8vw,4.75rem)] font-extrabold uppercase leading-[0.92]" data-id="element-1678" data-mp="4ba6petzzl1x">
            Activate your
            <br data-id="element-1679" data-mp="1ynqbeb75ar4o" />
            <span className="text-grit-500" data-id="element-1680" data-mp="1yn8ct7x0g0tl">GritVue X1™</span>
          </h1>
          <p className="mt-5 max-w-lg font-display text-xl font-bold uppercase leading-snug text-offwhite" data-id="element-1681" data-mp="26ofsgbw632wc">
            Make it official. Register your X1. Unlock owner resources. Help us build better gear.
          </p>
          <p className="mt-4 max-w-lg text-[15px] leading-relaxed text-steel-300" data-id="element-1682" data-mp="1wqruld39lf9y">
            Takes about a minute. Your answers tell us who uses the X1 and what to build next.
          </p>
          <Link to="/verify" className="mt-6 inline-flex items-center gap-1.5 text-[12px] font-bold uppercase tracking-[0.18em] text-grit-500 underline-offset-4 hover:underline" data-id="element-1683" data-mp="1nxeunr6s9ded">
            Verify your production batch
            <ArrowRightIcon className="h-4 w-4" aria-hidden="true" data-id="element-1684" data-mp="1dxea61x6gr8l" />
          </Link>
        </div>

        <div className="mt-10 lg:mt-0" data-id="element-1685" data-mp="1iwhfzokzf1hm">
          {status === 'success' ? <motion.div role="status" initial={{
          opacity: 0,
          y: 8
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.22,
          ease: [0.23, 1, 0.32, 1]
        }} data-id="element-1686" data-mp="14zcbht0x2aoh">
              <div className="border-2 border-grit-500 bg-grit-500/10 p-6 sm:p-8" data-id="element-1687" data-mp="qdyfrr041597">
                <p className="flex items-center gap-2 font-display text-3xl font-extrabold uppercase text-grit-500" data-id="element-1688" data-mp="lf3pk3ztcef2">
                  <BadgeCheckIcon className="h-8 w-8" aria-hidden="true" data-id="element-1689" data-mp="udp3tmy957a1" />
                  Your GritVue X1 is registered.
                </p>
                <p className="mt-2 text-[15px] text-steel-200" data-id="element-1690" data-mp="199y1ik2hvckc">Welcome to GritVue. Thanks for helping us build better gear.</p>
              </div>
              <ul className="mt-6 grid gap-3 sm:grid-cols-2" data-id="element-1691" data-mp="14a279cjlmzhz">
                {ownerActions.map(item => <li key={item.title} data-id="element-1692" data-mp="1e1odsvkzsaee">
                    <Link to={item.to} className="flex h-full items-center justify-between gap-3 border border-white/12 bg-ink-900 p-4 transition-colors duration-150 ease-grit hover:border-grit-500" data-id="element-1693" data-mp="1e2r5v6u1wivw">
                      <span data-id="element-1694" data-mp="kp87wyev6po8">
                        <span className="block font-display text-lg font-bold uppercase leading-tight text-offwhite" data-id="element-1695" data-mp="kpzrtbz3myyo">{item.title}</span>
                        <span className="mt-0.5 block text-[13px] text-steel-400" data-id="element-1696" data-mp="ooh1o39wmnjs">{item.body}</span>
                      </span>
                      <ArrowRightIcon className="h-4 w-4 shrink-0 text-grit-500" aria-hidden="true" data-id="element-1697" data-mp="yg39dpn1a4hs" />
                    </Link>
                  </li>)}
                <li data-id="element-1698" data-mp="13f6ch4jge0n4">
                  <a href="/#rx" className="flex h-full items-center justify-between gap-3 border border-white/12 bg-ink-900 p-4 transition-colors duration-150 ease-grit hover:border-grit-500" data-id="element-1699" data-mp="18ej2fy5mfntx">
                    <span data-id="element-1700" data-mp="1hdxtoztm42fh">
                      <span className="block font-display text-lg font-bold uppercase leading-tight text-offwhite" data-id="element-1701" data-mp="1hd4qg8tv3v23">Add RX capability</span>
                      <span className="mt-0.5 block text-[13px] text-steel-400" data-id="element-1702" data-mp="3j62fdtkiq3hr">Available now: RX Capability Insert, +$14.95</span>
                    </span>
                    <ArrowRightIcon className="h-4 w-4 shrink-0 text-grit-500" aria-hidden="true" data-id="element-1703" data-mp="3bp5y36bhhvjh" />
                  </a>
                </li>
              </ul>
            </motion.div> : <form onSubmit={submit} noValidate className="grid gap-6 border border-white/12 bg-ink-900 p-6 sm:p-8" data-id="element-1704" data-mp="31v6z2g6pirvr">
              <div className="grid gap-5 sm:grid-cols-2" data-id="element-1705" data-mp="3ulxb4y0fnef3">
                <div data-id="element-1706" data-mp="3ulf7hgbfxxx5">
                  <label htmlFor="act-order" className={labelClass} data-id="element-1707" data-mp="3ujulwg73tpbf">Order number</label>
                  <input id="act-order" required value={answers.orderNumber} onChange={e => update('orderNumber', e.target.value)} placeholder="#1001" autoComplete="off" className={inputClass} data-id="element-1708" data-mp="3pnne0hv5e3qu" />
                </div>
                <div data-id="element-1709" data-mp="3goignevbogg9">
                  <label htmlFor="act-email" className={labelClass} data-id="element-1710" data-mp="298cc9cmf3gf4">Email</label>
                  <input id="act-email" type="email" required value={answers.email} onChange={e => update('email', e.target.value)} placeholder="you@email.com" autoComplete="email" className={inputClass} data-id="element-1711" data-mp="2570m95zmst6e" />
                </div>
              </div>

              <div className="grid gap-5 border-t border-white/10 pt-6" data-id="element-1712" data-mp="d690yfv6kcto">
                <div data-id="element-1713" data-mp="3vbm0c0d85i5l">
                  <label htmlFor="act-use" className={labelClass} data-id="element-1714" data-mp="3vc43zi27uynj">What do you use your X1 for?</label>
                  <input id="act-use" value={answers.useCase} onChange={e => update('useCase', e.target.value)} placeholder="Engine bays, attic runs, night calls..." className={inputClass} data-id="element-1715" data-mp="45kldgq524eo" />
                </div>
                <div data-id="element-1716" data-mp="vvfpijinnyne">
                  <label htmlFor="act-trade" className={labelClass} data-id="element-1717" data-mp="vxs36khdrmun">What&apos;s your profession / trade?</label>
                  <input id="act-trade" value={answers.trade} onChange={e => update('trade', e.target.value)} placeholder="Electrician, mechanic, HVAC..." className={inputClass} data-id="element-1718" data-mp="n3nfub1ggtjv" />
                </div>
                <div data-id="element-1719" data-mp="1fjy98lkw6jup">
                  <label htmlFor="act-problem" className={labelClass} data-id="element-1720" data-mp="1fk6j04lpx91o">What problem did you most want X1 to solve?</label>
                  <input id="act-problem" value={answers.problem} onChange={e => update('problem', e.target.value)} placeholder="Holding a flashlight in my teeth..." className={inputClass} data-id="element-1721" data-mp="1oxfcuc2y2qbi" />
                </div>
              </div>

              <ChoiceChips legend="Which feature mattered most?" options={featureOptions} value={answers.feature} onChange={v => update('feature', v)} data-id="element-1722" data-mp="1yqnx9y663i8q" />
              <ChoiceChips legend="Where did you first hear about GritVue?" options={heardFromOptions} value={answers.heardFrom} onChange={v => update('heardFrom', v)} data-id="element-1723" data-mp="160xc5564bg34" />
              <ChoiceChips legend="Would you recommend X1 to a coworker?" options={recommendOptions} value={answers.recommend} onChange={v => update('recommend', v)} data-id="element-1724" data-mp="1fxs8bhex54e8" />
              <ChoiceChips legend="Do you wear prescription lenses?" options={prescriptionOptions} value={answers.wearsPrescription} onChange={v => update('wearsPrescription', v)} optional data-id="element-1725" data-mp="1b11tsyirigux" />

              <div data-id="element-1726" data-mp="2mg8d4870xzds">
                <button type="submit" disabled={!canSubmit || status === 'submitting'} className="flex w-full items-center justify-center gap-2 bg-grit-500 px-5 py-4 font-display text-xl font-extrabold uppercase tracking-wide text-ink-950 transition-[background-color,transform] duration-150 ease-grit hover:bg-grit-400 active:translate-y-[2px] disabled:opacity-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-offwhite" data-id="element-1727" data-mp="2bcbpnrj0ba3v">
                  {status === 'submitting' && <Loader2Icon className="h-5 w-5 animate-spin" aria-hidden="true" data-id="element-1728" data-mp="1nlpis3kqnvvg" />}
                  {status === 'submitting' ? 'Activating...' : 'Activate my X1'}
                </button>
                {!canSubmit && <p className="mt-2 text-[12px] text-steel-500" data-id="element-1729" data-mp="1dq4f8100unjl">Order number and a valid email are required.</p>}

                {status === 'error' && <p role="alert" className="mt-4 border border-white/20 bg-white/[0.03] p-4 text-[14px] text-steel-200" data-id="element-1730" data-mp="383ni7qctmyca">
                    Something went wrong and your activation wasn&apos;t saved. Try again, or email support@gritvuegear.com.
                  </p>}
                {status === 'not_connected' && <div role="status" className="mt-4 border border-white/20 bg-white/[0.03] p-4" data-id="element-1731" data-mp="3pdzqceqsazxg">
                    <p className="font-display text-lg font-bold uppercase text-offwhite" data-id="element-1732" data-mp="3ud3yd21z2rm7">Online activation is being connected</p>
                    <p className="mt-1 text-[14px] text-steel-300" data-id="element-1733" data-mp="31m2ewor8y9wv">
                      Your answers have not been saved yet. Send them by email and we&apos;ll register your X1 by hand.
                    </p>
                    <a href={activationMailto(answers)} className="mt-3 inline-flex items-center gap-2 border-2 border-grit-500 px-4 py-2.5 font-display text-base font-extrabold uppercase text-grit-500 transition-colors duration-150 ease-grit hover:bg-grit-500 hover:text-ink-950" data-id="element-1734" data-mp="3bl1gjndlh5ad">
                      <MailIcon className="h-4 w-4" aria-hidden="true" data-id="element-1735" data-mp="14aompmthgfz" />
                      Email my activation
                    </a>
                  </div>}
                <p className="mt-4 text-[12px] leading-relaxed text-steel-500" data-id="element-1736" data-mp="b3082mud38yc">
                  We use your answers to support your X1 and improve our products and advertising. We never sell your data.{' '}
                  <Link to="/legal/privacy" className="underline underline-offset-2 hover:text-steel-300" data-id="element-1737" data-mp="3eljngorxykbq">Privacy policy</Link>
                </p>
              </div>
            </form>}
        </div>
      </main>
      <SiteFooter data-id="element-1738" data-mp="3ocxwpo9p2yi2" />
    </div>;
}